<template>
  <v-container fluid>
    <v-row>
      <v-breadcrumbs class="pa-0 text--text caption">
        <router-link
          class="rlink-hover text--text"
          style="text-decoration: none;"
          :to="{ path: '/sales/'}"
        >
          <span class="ml-1">Sales</span>
        </router-link>
        <span class="pl-2 pr-2">/</span>
        <v-breadcrumbs-item disabled>{{ id }}</v-breadcrumbs-item>
      </v-breadcrumbs>
    </v-row>

    <v-row class="align-center mt-4 title mb-4">

      <v-spacer></v-spacer>

      <t-item-diff v-if="$store.state.settings.debugMode" :item1="lastSave" :item2="merged">
        <v-layout 
        slot="item1Header"
        align-center class="subheading font-weight-bold pl-2 crx-toolbar">
          Last save
        </v-layout>
        <v-layout 
        slot="item2Header"
        align-center class="subheading font-weight-bold pl-2 crx-toolbar">
          LIVE
        </v-layout>
      </t-item-diff>

      <v-btn small rounded outlined :to="{ path: `/print/packing-slip/${id}?lang=eng` }" target="_blank">
        <v-icon>mdi-file-pdf-box</v-icon> Packing slip
      </v-btn>

      <v-slide-x-transition mode="out-in">
        <v-btn v-if="isDirty" text outlined key="save2" width="8em" :loading="saving" @click="save">
          <v-icon color="color1">mdi-cloud-upload-outline</v-icon>
          <div class="ml-1">Save</div>
        </v-btn>

        <v-btn v-else key="save1" text width="8em" class="font-weight-regular" disabled>
          <v-icon small>mdi-shield-check-outline</v-icon>Saved
        </v-btn>
      </v-slide-x-transition>

      <v-btn
      v-if="sale.stage !== 1"
      text
      color="color1"
      :loading="completing"
      @click="completeSaleClicked">
        Complete
      </v-btn>
    </v-row>

    <!-- PAGE -->
    <v-card class="ma-4">
      
      <v-row class="pa-4 align-center">
        <div class="display-1">
          Sale: <span class="">{{ id }}</span>
        </div>
        &nbsp;&nbsp;
        <v-chip v-if="sale.stage === 0" small color="blue" text-color="white" class="caption">In process</v-chip>
        <v-chip v-else-if="sale.stage === 1" small color="green" text-color="white" class="caption">Complete</v-chip>

        <v-spacer></v-spacer>
      </v-row>

      <v-row>
        <v-col md="6">
          <v-card outlined class="mr-2">
            <div class="title pa-4">Sale info</div>

            <div class="c-hr"></div>

            <div class="pa-4">
              <v-row class="align-center">
                <v-col md="3">
                  <div class="label">Customer:</div>
                </v-col>
                <v-col md="9">
                  <c-select-customer 
                  :selectedID.sync="formData.customer_id"
                  @change="changeDetected"
                  class="crx-box-input">
                  </c-select-customer>
                </v-col>
              </v-row>

              <v-row class="align-center">
                <v-col md="3">
                  <div class="label">Date shipped:</div>
                </v-col>
                <v-col md="9">
                  <c-datetime :date.sync="formData.date" class="crx-box-input"></c-datetime>
                </v-col>
              </v-row>

              <v-row class="align-start">
                <v-col md="3">
                  <div class="label">Shipped with:</div>
                </v-col>
                <v-col md="9">
                  <c-input :value.sync="formData.shipped_with" class="crx-box-input"></c-input>
                  <div class="label">
                    e.g. POSTA, DHL
                  </div>
                </v-col>
              </v-row>

              <v-row class="align-start">
                <v-col md="3">
                  <div class="label">Order number:</div>
                </v-col>
                <v-col md="9">
                  <c-input :value.sync="formData.order_number" class="crx-box-input"></c-input>
                  <div class="label">
                    e.g. invoice number
                  </div>
                </v-col>
              </v-row>

            </div>
          </v-card>
        </v-col>
        <v-col sm="6">
          <v-card outlined>
            <div class="title pa-4">
              Shipping info
            </div>
            <div class="c-hr"></div>

            <div class="pa-4">

              <v-layout align-start wrap class="mt-3">
                <v-flex xs12 md3>
                  <div class="label">Ship to:</div>
                </v-flex>
                <v-flex xs12 md9>
                  <c-textarea :value.sync="formData.shipping_addr"></c-textarea>
                </v-flex>
              </v-layout>

            </div>

          </v-card>
        </v-col>
      </v-row>

      <v-tabs
      v-model="tabView"
      background-color="red lighten-2"
      dark
      >
        <v-tab>
          Boxed
        </v-tab>
        <v-tab>
          Combined
        </v-tab>
      </v-tabs>

      <!-- FIGURE THIS OUT -->
      <div v-if="$accessControl.userHasAccess(0, $store.state.auth.accessControl)" class="background caption pa-4">
        Figure out double entry error, 
      </div>

      <v-tooltip bottom v-if="$accessControl.userHasAccess(0, $store.state.auth.accessControl)">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" @click="openSettingsDialog">
            <v-icon>
              mdi-settings
            </v-icon>
          </v-btn>
        </template>
        <span>Settings</span>
      </v-tooltip>

      <!-- Entire sale - merged -->
      <v-card v-show="tabView === 1" outlined class="ma-4">
        <box-contents-parent 
        :itemSource.sync="items" 
        :activeBox="null"
        :boxOrderIDMap="boxOrderIDMap"
        :savedInputs="savedInputs"
        :mergeMap="mergeMap"
        :inventoryView="inventoryView"
        :singleMode="false"></box-contents-parent>
      </v-card>
      <!-- Entire sale - merged -->

      <v-row v-show="tabView === 0">
        <v-col sm="4">
          <v-card outlined>
            <v-row class="pa-2">
              BOXES
            </v-row>

            <!-- Box content area -->
            <box-card 
            :itemSource.sync="boxItems" 
            :activeBox.sync="activeBox"
            :saleItems="items"
            ref="boxCard"></box-card>
  
          </v-card>
        </v-col>
        <v-col sm="8">
          <v-card outlined class="pa-2">
            <!-- if activeBox === null -->
            <div v-if="activeBox === null">
              Select a box
            </div>
            <div v-else>
              <v-row class="pa-2">
                BOX {{ activeBoxOrder + 1 }}
              </v-row>

              <!-- BOX CONTENT HERE -->
              <box-contents-parent 
              :itemSource.sync="items" 
              :activeBox="activeBox"
              :boxOrderIDMap="boxOrderIDMap"
              :savedInputs="savedInputs"
              :mergeMap="mergeMap"
              :inventoryView="inventoryView"></box-contents-parent>
              <!-- BOX CONTENT HERE -->
            </div>
  
          </v-card>
        </v-col>
      </v-row>

    </v-card>

    <!-- LOG AREA -->

    <c-expansion-panel v-if="$accessControl.userHasAccess(0, $store.state.auth.accessControl)">
      <template v-slot:header="{ expand }">
        <v-row class="pa-3 align-center border-b--thin">
          <div>
           Log Area
          </div>
          <v-spacer></v-spacer>
          <v-icon :class="{'rotate-180': expand}">mdi-chevron-down</v-icon>
        </v-row>
      </template>
      <template v-slot:body>
        <div>
          <v-card class="ma-4 caption unselectable">
            box Items:
            <json-viewer :item="boxItems"></json-viewer>
          </v-card>

          <v-card class="ma-4 caption unselectable">
            Items:
            <json-viewer :item="itemsWithMappedBox"></json-viewer>
          </v-card>

          <v-card class="ma-4 caption unselectable">
            Inventory View:
            <json-viewer :item="inventoryView"></json-viewer>
          </v-card>

          <v-row class="pa-2">
            Figure this out! and prevent 3.9999999 issue
          </v-row>
          <v-row class="pa-2">
            Quantity per box: 12<br />
            Total Quantity: 4
          </v-row>

          <v-card class="ma-4 caption unselectable">
            Inventory Map:
            <json-viewer :item="inventoryMap"></json-viewer>
          </v-card>

          <v-row no-gutters>
            <v-col sm="4">
              <v-card class="ma-1 caption unselectable">
                Saved Inputs:
                <json-viewer :item="savedInputs"></json-viewer>
              </v-card>
            </v-col>
            <v-col sm="4">
              <v-card class="ma-1 caption unselectable">
                Merge Map:
                <json-viewer :item="mergeMap"></json-viewer>
              </v-card>
            </v-col>
            <v-col sm="4">
              <v-card class="ma-1 caption unselectable">
                DEBIT Map:
                <json-viewer :item="debitMap"></json-viewer>
              </v-card>
            </v-col>
            <v-col sm="4">
              <v-card class="ma-1 caption unselectable">
                Inventory Map:
                <json-viewer :item="inventoryMap"></json-viewer>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </template>
    </c-expansion-panel>
    <!-- LOG AREA -->

    <!-- DIALOGS -->
    <settings-dialog ref="settingsDialog"></settings-dialog>

  </v-container>
</template>

<script>
import Vue from "vue"
import axios from "axios"
import ListRow from "./list-row"
import BoxCard from "./box-card"
import BoxRow from "./box-row"
import BoxContentsParent from "./box-contents-parent"
import ListController from "@/mixins/ListController"
import InventoryHelper from "@/js/shared/inventory.js"
import DragTableHeaders from '@/mixins/DragTableHeaders'
import SettingsDialog from '@/components/dialogs/table-columns-edit-schema/Dialog'

export default {
  props: {
    id: null
  },
  mixins: [ListController, DragTableHeaders],
  components: {
    BoxRow,
    BoxCard,
    ListRow,
    BoxContentsParent,
    SettingsDialog
  },
  data() {
    return {
      singleMode: false,
      tabView: 0,
      formData: {
        customer_id: null,
        date: null,
        shipping_addr: null,
        shipped_with: null,
        order_number: null
      },
      lastSave: {
        customer_id: null,
        date: null,
        shipping_addr: null,
        shipped_with: null,
        order_number: null,
        rows: []
      },
      filters: {
        rowsPerPage: 10,
        sortCol: "name",
        sortAscending: true,
        date: {
          mainSelect: "is equal to",
          isInTheLast_select: "days",
          isInTheLast_input: null,
          date1: null,
          date2: null
        },
        search: {
          id: "",
          name: ""
        },
        active: {
          customer: false,
          date: false,
          deleted: false
        },
        categories: []
      },
      page: 0,
      saving: false,
      completing: false,
      inventoryMap: {},
      debitMap: {},
      mergeMap: {},
      savedInputs: [],
      sale: {},
      activeBox: null, // The actively selected box (when you click on a box this var will be set equal to its tempID)
      boxItems: []
    };
  },
  computed: {
    inventoryExceptions() {
      return this.items.map(item => {
        if (item.inventory_id) {
          return item.inventory_id;
        } else {
          return this.mergeMap[item.id];
        }
      });
    },
    isDirty() {
      return !_.isEqual(this.lastSave, this.merged);
    },
    merged() {
      var merged = {
        customer_id: this.formData.customer_id,
        date: this.formData.date,
        shipping_addr: this.formData.shipping_addr,
        shipped_with: this.formData.shipped_with,
        order_number: this.formData.order_number,
        rows: JSON.parse(JSON.stringify(this.itemsWithMappedBox))
      };
      return merged;
    },
    inventorySet() {
      return [...new Set(this.items.map(item => item.inventory_item_id))];
    },
    activeBoxOrder () {
      return this.boxItems.find(item => item.tempID === this.activeBox).ordering
    },
    activeBoxItems () {
      return this.items.filter(item => item.box === this.activeBox)
    },
    itemMap () {
      var map ={}
      this.items.forEach(item => {
        if (!map[item.inventory_item_id]) {
          map[item.inventory_item_id] = {
            definition: item.definition,
            items: [item]
            }
        } else {
          map[item.inventory_item_id].items.push(item)
        }
      })
      return map
    },
    inventoryView() {
      var inventoryArray = Object.values(this.inventoryMap).flat(1);
      return InventoryHelper.mergeDebitsBackToInventory(
        inventoryArray,
        this.savedInputs,
        this.mergeMap
      ).reduce((a, i) => {
        if (a[i.inventory_item_id]) {
          a[i.inventory_item_id].push(i);
        } else {
          a[i.inventory_item_id] = [i];
        }
        return a;
      }, {});
    },
    boxOrderIDMap () {
      // map = {item.box: ordering}
      var map = {}
      this.items.forEach(item => {
        var boxRow = this.boxItems.find(boxItem => boxItem.tempID === item.box) || {}
        map[item.box] = boxRow.ordering + 1
      })
      return map
    },
    itemsWithMappedBox () {
      // This is this.items but with box set to the actual box ordering
      var wtf = JSON.parse(JSON.stringify(this.items))
      return wtf.map(item => {
        // var boxRow = this.boxItems.find(boxItem => boxItem.tempID === item.box) || {}
        // item.box = boxRow.ordering + 1
        item.box = this.boxOrderIDMap[item.box]
        return item
      })
    }
  },
  watch: {
    inventorySet(nv) {
      Object.keys(this.debitMap).forEach(_id => {
        var id = parseInt(_id);
        if (nv.indexOf(id) === -1) {
          Vue.delete(this.debitMap, id);
        }
      })
      this.fetchInventory(this.inventorySet).then(map => {
        this.inventoryMap = map;
      })
    },
    id (nv) {
      this.loadAPI()
    },
    boxItems (nv) {
      var remainingTempIDs = nv.map(item => item.tempID)
      var remainingBoxIDs = [... new Set(this.items.map(item => item.box))]
      var toDelete = remainingBoxIDs.filter(id => !remainingTempIDs.includes(id))

      this.items = this.items.filter(item => !toDelete.includes(item.box))
      if (!remainingTempIDs.includes(this.activeBox)) {
        if (this.boxItems.length) {
          this.activeBox = this.boxItems[0].tempID
        } else {
          this.activeBox = null
        }
      }
    }
  },
  methods: {
    openSettingsDialog () {
      this.$refs.settingsDialog.open('sales_column_schema')
    },
    changeDetected (customer) {
      if (customer) {
        this.formData.shipping_addr = customer.document_block
      }
    },
    convertDBInputsToDebitMaps(inputs, mergeMap) {
      var rmMap = {};
      inputs.forEach(input => {
        if (!rmMap[input.inventory_item_id]) {
          rmMap[input.inventory_item_id] = {};
        }
        var invMergeId = mergeMap[input.id];
        var currentDebit = rmMap[input.inventory_item_id][invMergeId] || 0;
        Vue.set(
          rmMap[input.inventory_item_id],
          invMergeId,
          currentDebit + input.quantity
        );
      });
      return rmMap;
    },
    fetchInventory(_set) {
      return new Promise((resolve, reject) => {
        this.isLoading = true;
        this.$axios
          .post(`/inventory/inventory-item-id/multiple`, _set)
          .then(response => {
            resolve(response.data.map);
          })
          .catch(error => {
            console.log(error);
            this.$root.$errorSnackbar(error);
            reject(error);
          });
      });
    },
    save() {
      this.saveAxios()
        .then(() => {
          this.loadAPI();
        })
        .catch(error => this.$root.$errorSnackbar(error));
    },

    /* saveAxios () {
      return new Promise((resolve, reject) => {
        let data = {
          customerID: this.formData.customerID,
          rows: JSON.parse(JSON.stringify(this.items)),
          mergeMap: this.mergeMap
        }
        this.saving = true
        this.$axios.patch('sales/sale/' + this.id,
          data,
          {headers: {'Content-Type': 'application/json'}})
          .then(response => {
            resolve()
          })
          .catch(error => {
            console.log(error)
            reject(error)
          })
          .then(() => {
            setTimeout(() => {
            this.saving = false
            }, 200)
          })
      })
    }, */

    saveAxios() {
      return new Promise((resolve, reject) => {
        var prunedRows = this.items.map(item => {
          var boxRow = this.boxItems.find(boxItem => boxItem.tempID === item.box)
          return {
            id: item.id,
            box: boxRow.ordering + 1,
            inventory_id: item.inventory_id,
            inventory_item_id: item.inventory_item_id,
            quantity_per_box: item.quantity_per_box,
            quantity: item.quantity,
            archive_id: item.archive_id,
            weight: item.weight,
            json_data: item.json_data,
            custom_columns: item.custom_columns
          };
        });
        let data = {
          customer_id: this.formData.customer_id,
          date: this.formData.date,
          shipping_addr: this.formData.shipping_addr,
          shipped_with: this.formData.shipped_with,
          order_number: this.formData.order_number || null,
          rows: JSON.parse(JSON.stringify(prunedRows)),
          mergeMap: this.mergeMap || {}
        };
        this.saving = true;
        // log the save object
        console.log('SAVING')
        console.log(data)
        this.$axios
          .patch("sales/sale/" + this.id, data, {
            headers: { "Content-Type": "application/json" }
          })
          .then(response => {
            resolve();
          })
          .catch(error => {
            console.log(error);
            this.$root.$errorSnackbar(error);
            reject(error);
          })
          .then(() => {
            setTimeout(() => {
              this.saving = false;
            }, 200);
          });
      });
    },
    completeSaleClicked () {
      this.$root.$confirm('Complete Sale?', 'Are you sure', { color: 'green' })
        .then((confirm) => {
          if (confirm) {
            this.completeSale()
          }
        })
    },
    completeSale () {
      this.completing = true
      this.saveAxios()
        .then(res => {
          return this.completeSaleAxios()
          .then(() => {
            this.loadAPI()
          })
        })
        .catch(error => this.$root.$errorSnackbar(error))
        .then(() => {
          this.completing = false
        })
    },
    completeSaleAxios () {
      return new Promise((resolve, reject) => {
        this.completing = true
        this.$axios.post('sales/' + this.id + '/complete',
          {id: this.id},
          {headers: {'Content-Type': 'application/json'}})
          .then(() => {
            resolve()
          })
          .catch(error => {
            console.log(error)
            this.$root.$errorSnackbar(error)
            reject(error)
          })
          .then(() => {
            this.completing = false
          })
      })
    },
    loadAPI: _.debounce(
      function(e) {
        return new Promise((resolve, reject) => {
          this.isLoading = true;
          this.boxItems = []
          //this.$axios.get(`/inventory-tx/${this.id}`)
          axios
            .all([
              this.$axios.get("/sales/" + this.id),
              this.$axios.get("/sales-items/" + this.id)
            ])
            .then(
              axios.spread((sale, salesItems) => {
                this.items = salesItems.data.rows
                this.items.map(item => {
                  Vue.set(item, 'box_count', 0)
                  item.json_data = JSON.parse(item.json_data)
                  item.custom_columns = JSON.parse(item.custom_columns) || {}
                  return item
                })

                var boxCount = this.items.reduce((a, i) => i.box > a ? i.box : a, null)
                for (var i = 1; i <= boxCount; i++) {
                  this.boxItems.push({
                    tempID: i
                  })
                }

                if (this.boxItems.length) { // seems no boxes results in infinity
                  this.$refs.boxCard.tempIDcounter = boxCount
                }

                this.sale = sale.data

                this.formData.date = sale.data.date || this.$moment()
                this.formData.customer_id = sale.data.customer
                this.formData.shipping_addr = sale.data.shipping_addr || ''
                this.formData.shipped_with = sale.data.shipped_with || ''
                this.formData.order_number = sale.data.order_number || null

                // nextTick because of rows getting .tempID and .ordering properties after this is called
                this.$nextTick(() => {
                  this.lastSave.date = sale.data.date
                  this.lastSave.customer_id = sale.data.customer;
                  this.lastSave.shipping_addr = sale.data.shipping_addr || ''
                  this.lastSave.shipped_with = sale.data.shipped_with || ''
                  this.lastSave.order_number = sale.data.order_number || null
                  this.lastSave.rows = JSON.parse(JSON.stringify(this.items));
                });

                this.savedInputs = JSON.parse(JSON.stringify(this.items));

                var invSet = [
                  ...new Set(this.items.map(item => item.inventory_item_id))
                ];
                return this.$axios.post(
                  `/inventory/inventory-item-id/multiple`,
                  invSet
                );
              })
            )
            .then(response => {
              var invMap = response.data.map;
              var inventoryArray = Object.values(invMap).flat(1);
              this.mergeMap = InventoryHelper.mergeMapMultipleNew(
                inventoryArray,
                this.savedInputs
              );
              this.debitMap = this.convertDBInputsToDebitMaps(
                this.savedInputs,
                this.mergeMap
              );
            })
            .then(() => resolve())
            .catch(error => {
              console.log(error);
              this.$root.$errorSnackbar(error);
              reject(error);
            })
            .then(() => {
              this.isLoading = false;
            });
        });
      },
      150,
      { leading: true }
    )
  },
  created() {
    this.loadAPI();
  },
  mounted () {
    this.colGroupRefs.push(this.$refs.colgroup1)
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('mouseup', this.mouseUp)
  }
};
</script>